import { Box, VStack } from '@chakra-ui/react';
import { keyframes } from '@chakra-ui/system';

import Button from 'components/Button';
import Icon from 'components/Icon';
import Modal from 'components/Modal';
import Text from 'components/Text';
import { Plan_Type_Enum } from 'types/databaseEnums';
import { IModal } from 'types/frontend';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const getWelcomeMessage = (
  plan: Plan_Type_Enum,
  trialChargeDate: string | null
): { title: string; description: string } => {
  if (plan === Plan_Type_Enum.Lifetime) {
    return {
      title: 'Lifetime plan activated!',
      description: 'You have unlimited access to all features, forever.',
    };
  }

  if (trialChargeDate) {
    return {
      title: 'Your 7-day free trial has started!',
      description: `You'll be charged on ${trialChargeDate}. Cancel anytime from billing.`,
    };
  }

  return {
    title: 'Your plan is now active!',
    description: 'You have full access to all features.',
  };
};

export const shouldShowWelcomeModal = (
  hasWelcomeParam: boolean,
  alreadyShown: boolean
): boolean => {
  if (!hasWelcomeParam) return false;
  if (alreadyShown) return false;
  return true;
};

const WELCOME_MODAL_SHOWN_SUFFIX = '-welcome-modal-shown';

export const getWelcomeModalStorageKey = (userId: string) =>
  `${userId}${WELCOME_MODAL_SHOWN_SUFFIX}`;

interface IWelcomeModalProps extends IModal {
  plan: Plan_Type_Enum;
  isTrial: boolean;
}

export const WelcomeModal = ({ isOpen, onClose, plan, isTrial }: IWelcomeModalProps) => {
  const trialChargeDate = isTrial
    ? new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  const { title, description } = getWelcomeMessage(plan, trialChargeDate);

  const handleStart = () => {
    onClose();
  };

  return (
    <Modal size="xl" isOpen={isOpen} onClose={onClose} header={null} footer={null} blockScrollOnMount={false}>
      <VStack spacing={5} py={4} px={1}>
        {/* Header */}
        <VStack spacing={3} animation={`${fadeIn} 0.5s ease-out`}>
          <Box
            p={3}
            borderRadius="full"
            background="linear-gradient(135deg, rgba(159, 122, 234, 0.2) 0%, rgba(237, 137, 54, 0.2) 100%)"
            border="1px solid"
            borderColor="whiteAlpha.200"
          >
            <Icon type="Rocket" size={28} />
          </Box>
          <VStack spacing={1}>
            <Text fontSize="xLarge" fontWeight="bold">
              Welcome to BigDevSoon!
            </Text>
            <Text fontWeight="semibold" opacity="default" fontSize="medium" textAlign="center">
              {title}
            </Text>
            <Text fontWeight="regular" opacity="secondary" fontSize="small" textAlign="center">
              {description}
            </Text>
          </VStack>
        </VStack>

        {/* Missions hint */}
        <Text
          fontSize="small"
          opacity="tertiary"
          textAlign="center"
          fontStyle="italic"
          animation={`${fadeIn} 0.5s ease-out 0.1s both`}
        >
          Check the bottom-right corner for extra missions.
        </Text>

        {/* CTA */}
        <VStack spacing={3} w="100%" animation={`${fadeIn} 0.5s ease-out 0.2s both`}>
          <Button scheme="gradient" width="100%" onClick={handleStart}>
            Start your journey
          </Button>
        </VStack>
      </VStack>
    </Modal>
  );
};
